import React, { useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Upload, Loader2, X, Camera as CameraIcon } from "lucide-react";
import { toast } from "sonner";
import { capturePhoto, ensureCameraPermission } from "../lib/native/camera";
import { isNative } from "../lib/native/platform";

export default function ImageUpload({ value, onChange, folder = "misc", testid = "image-upload", label }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.type && !["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Formato non supportato. Usa JPEG, PNG o WEBP."); return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5MB)."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", folder);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setUploading(false);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    uploadFile(file);
  };

  const takePhoto = async () => {
    const ok = await ensureCameraPermission();
    if (!ok) { toast.error("Permesso fotocamera negato"); return; }
    const res = await capturePhoto({ source: "camera" });
    if (res?.file) uploadFile(res.file);
  };

  return (
    <div data-testid={testid} className="space-y-2">
      {label && <div className="text-sm text-zinc-300">{label}</div>}
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative">
            <img src={value} alt="anteprima" className="h-16 w-16 rounded-md object-cover border border-white/10" />
            <button type="button" data-testid={`${testid}-clear`} onClick={() => onChange("")}
              className="absolute -top-1.5 -right-1.5 bg-red-600 rounded-full p-0.5 text-white"><X className="h-3 w-3" /></button>
          </div>
        ) : (
          <div className="h-16 w-16 rounded-md border border-dashed border-white/15 flex items-center justify-center text-zinc-600 text-xs">nessuna</div>
        )}
        <Button type="button" size="sm" variant="secondary" data-testid={`${testid}-btn`} onClick={pick} disabled={uploading}>
          {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
          {uploading ? "Caricamento…" : "Carica immagine"}
        </Button>
        <input ref={inputRef} data-testid={`${testid}-input`} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} />
        {isNative() && (
          <Button type="button" size="sm" variant="secondary" data-testid={`${testid}-camera`} onClick={takePhoto} disabled={uploading}>
            <CameraIcon className="h-4 w-4 mr-1" /> Scatta
          </Button>
        )}
      </div>
      <Input data-testid={`${testid}-url`} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-[#0A0A0C] border-white/10" placeholder="oppure incolla un URL https://…" />
    </div>
  );
}
